import { get, patch, post } from '@spark/services/http';

const apiBase = (process.env.REACT_APP_BASE_API_URL_SYNC || 'http://localhost:32768').replace(/\/$/, '');
const endpoint = `${apiBase}/api/v1/programacao_havan`;

const unwrap = response => response?.data || response || {};

export const listarProgramacaoHavanDatas = async () => unwrap(await get(`${endpoint}/datas`));

// somente_protocolo: só as cargas marcadas como protocolo (tela Emissão de protocolo).
export const listarProgramacaoHavan = async ({ data_programacao, turno, pagina = 1, tamanho_pagina = 20, somente_protocolo = false }) => {
  const query = new URLSearchParams({ data_programacao, turno: String(turno), pagina: String(pagina), tamanho_pagina: String(tamanho_pagina) });
  if (somente_protocolo) query.set('somente_protocolo', 'true');
  return unwrap(await get(`${endpoint}?${query.toString()}`));
};

const enviarPlanilha = async (caminho, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return unwrap(await post(`${endpoint}/${caminho}`, formData));
};

export const importarProgramacaoHavan = file => enviarPlanilha('importacoes', file);

// Definitiva: a pré-visualização só compara com a programação da data; a aplicação grava.
export const previsualizarDefinitivaProgramacaoHavan = file => enviarPlanilha('importacoes/definitiva/previsualizacao', file);

export const aplicarDefinitivaProgramacaoHavan = file => enviarPlanilha('importacoes/definitiva', file);

export const atualizarProgramacaoHavan = async (id, dados) => unwrap(await patch(`${endpoint}/${id}`, dados));

// Filiais que já vieram nas programações (código, nome e UF): o número da filial vem pela escolha.
export const listarFiliaisProgramacaoHavan = async () => unwrap(await get(`${endpoint}/filiais`));

// Carga incluída manualmente (sem planilha): obrigatórios data, número da carga e filial. Sem notificação automática:
// a tela mostra o erro da API dentro do próprio modal.
export const incluirCargaManualProgramacaoHavan = async dados => unwrap(await post(`${endpoint}/cargas`, dados, {
  headers: { 'x-skip-notification': 'true' }
}));

// Auditoria geral: filtros vazios não vão na query (a API trata ausência como "não filtrar").
export const listarAuditoriaProgramacaoHavan = async (filtros = {}) => {
  const query = new URLSearchParams(Object.entries(filtros)
    .filter(([, valor]) => valor !== undefined && valor !== null && String(valor).trim() !== '')
    .map(([chave, valor]) => [chave, String(valor)]));
  return unwrap(await get(`${endpoint}/auditorias?${query.toString()}`));
};

// "Gestor avisado" (linha verde forte): a API grava quem avisou e quando; endpoint próprio, como o protocolo.
export const atualizarGestorAvisadoProgramacaoHavan = async (id, gestorAvisado) => unwrap(await patch(`${endpoint}/${id}/gestor_avisado`, { gestor_avisado: gestorAvisado }));

// Marcação "Protocolo" (linha roxa): endpoint próprio, não passa pela atualização de motorista/conjunto.
export const atualizarProtocoloProgramacaoHavan = async (id, protocolo) => unwrap(await patch(`${endpoint}/${id}/protocolo`, { protocolo }));

// Etapas do protocolo (tela Emissão de protocolo): etapa 'recebido' ou 'entregue'. A API recusa (409) fora de ordem.
export const atualizarEtapaProtocoloProgramacaoHavan = async (id, etapa, marcado) => unwrap(await patch(`${endpoint}/${id}/protocolo_etapa`, { etapa, marcado }));

// Observação livre do operador (vazia apaga): endpoint próprio, não passa pela atualização de motorista/conjunto.
export const atualizarObservacaoProgramacaoHavan = async (id, observacao) => unwrap(await patch(`${endpoint}/${id}/observacao`, { observacao }));

export const consultarVxProgramacaoHavan = async (id, dados) => unwrap(await post(`${endpoint}/${id}/consultar_vx`, dados, {
  headers: { 'x-skip-notification': 'true' }
}));

export const buscarPreparacaoVxProgramacaoHavan = async id => unwrap(await get(`${endpoint}/${id}/vx_preparacao`));

export const buscarOpcoesCargaVxProgramacaoHavan = async () => unwrap(await get(`${endpoint}/vx_carga_opcoes`));

export const buscarMotoristasProgramacaoHavan = async (id, termo) => unwrap(await get(`${endpoint}/${id}/motoristas?${new URLSearchParams({ termo }).toString()}`));

// tipo: 'tracao' (Frota/Placa cavalo) ou 'reboque' (carretas); sem tipo a API traz todos os veículos.
export const buscarVeiculosProgramacaoHavan = async (id, termo, tipo) => unwrap(await get(`${endpoint}/${id}/veiculos?${new URLSearchParams(tipo ? { termo, tipo } : { termo }).toString()}`));

// Cavalo e carretas atuais do motorista no TMS (origem 'tms'); com origem 'banco' nada vem preenchido e vale a sugestão.
export const buscarConjuntoMotoristaProgramacaoHavan = async (id, codigoMotorista) => unwrap(await get(`${endpoint}/${id}/motoristas/${codigoMotorista}/conjunto`, {
  headers: { 'x-skip-notification': 'true' }
}));

// Carretas do cavalo escolhido: vínculo atual do TMS (origem 'tms') ou, se o TMS não responder, o do banco (origem 'banco').
export const buscarConjuntoVeiculoProgramacaoHavan = async (id, placa) => unwrap(await get(`${endpoint}/${id}/veiculos/conjunto?${new URLSearchParams({ placa }).toString()}`, {
  headers: { 'x-skip-notification': 'true' }
}));

export const buscarEnderecoPorCep = async cep => {
  const digitos = String(cep || '').replace(/\D/g, '');
  if (digitos.length !== 8) return null;
  const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
  const dados = await resposta.json();
  return dados?.erro ? null : { logradouro: dados.logradouro, bairro: dados.bairro, cidade: dados.localidade, estado: dados.uf };
};

export const baixarLaudoVxProgramacaoHavan = async id => {
  const response = await get(`${endpoint}/${id}/vx_laudo`, { responseType: 'blob' });
  const disposition = response?.headers?.['content-disposition'] || '';
  const nomeArquivo = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)?.[1];
  return { blob: response?.data, nomeArquivo: nomeArquivo ? decodeURIComponent(nomeArquivo) : `laudo-vx-${id}.pdf` };
};

// Cadastro no TMS de motorista/placa fora do TMS (configuração CadastroTms da API: ligado/desligado e ambiente).
export const obterConfiguracaoCadastroTms = async () => unwrap(await get(`${endpoint}/cadastro_tms/configuracao`));

export const cadastrarMotoristaTms = async (id, dados) => unwrap(await post(`${endpoint}/${id}/cadastro_tms/motorista`, dados, {
  headers: { 'x-skip-notification': 'true' }
}));

export const cadastrarVeiculoTms = async (id, dados) => unwrap(await post(`${endpoint}/${id}/cadastro_tms/veiculo`, dados, {
  headers: { 'x-skip-notification': 'true' }
}));
